import { supabase } from './supabase'

/**
 * The number on the app icon.
 *
 * This is as close to a home-screen widget as a web app on iOS can get, and it
 * is worth being exact about why. A real widget needs WidgetKit, which needs a
 * native app, which needs the App Store — all three explicitly out of scope in
 * CLAUDE.md. There is no web API for it and no workaround; anyone who tells you
 * otherwise is describing a native wrapper.
 *
 * What iOS 16.4 *did* give home-screen web apps is the Badging API: one number
 * on the icon. Not a widget, but it answers the same question a widget would —
 * *is there anything for me* — without opening anything.
 *
 * Two conditions, both already true here: the app must be installed to the home
 * screen, and notification permission must be granted. In Safari as a tab this
 * silently does nothing, which is the correct behaviour rather than a failure.
 */

/** What the number counts: things that are actually waiting for this person. */
export async function badgeCount(userId: string, isAdmin: boolean): Promise<number> {
  const tasks = supabase
    .from('tasks')
    .select('id', { count: 'exact', head: true })
    .is('done_at', null)
    .is('deleted_at', null)

  // A member sees only what was handed to them. The administrator's number
  // includes what nobody has picked up, because unassigned means theirs.
  const mine = isAdmin
    ? tasks.or(`assigned_to.is.null,assigned_to.eq.${userId}`)
    : tasks.eq('assigned_to', userId)

  const [taskCount, docCount] = await Promise.all([
    mine,
    // `verified_at` lives on the obligation, not the document — the thing that
    // needs a human is the reading, not the paper.
    isAdmin
      ? supabase
          .from('obligations')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null)
          .is('verified_at', null)
      : Promise.resolve({ count: 0 }),
  ])

  return Number(taskCount.count ?? 0) + Number((docCount as { count: number | null }).count ?? 0)
}

/**
 * Put it on the icon, or take it off.
 *
 * Every failure here is silent and that is deliberate: an unsupported browser,
 * a tab instead of an installed app, permission not granted. None of those are
 * errors the person needs to hear about, and none of them should stop the
 * screen behind this from rendering.
 */
export async function showBadge(count: number): Promise<void> {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>
      clearAppBadge?: () => Promise<void>
    }
    if (count > 0) await nav.setAppBadge?.(count)
    else await nav.clearAppBadge?.()
  } catch {
    // Not installed, not permitted, or not supported. All fine.
  }
}

/** Read the counts and paint the icon. Safe to call often. */
export async function refreshBadge(userId: string, isAdmin: boolean): Promise<void> {
  try {
    await showBadge(await badgeCount(userId, isAdmin))
  } catch {
    // A badge that cannot be computed must never take a screen down with it.
  }
}
